import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel,
  MenuItem, Select, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { adminSpam } from './api.js'
import SpamRuleDialog from './SpamRuleDialog.jsx'
import { intentOptionsFor } from './constants/intents.js'
import { KNOWN_CHANNELS, CHANNEL_LABELS } from './constants/channels.js'
import { KNOWN_CLIENTS } from './constants/clients.js'
import { KNOWN_COMPANIES } from './constants/companies.js'
import { matchesSpamRuleSearch } from './spamRuleSearch.js'
import { buildConditionSummary } from './spamRuleConditions.js'
import { TEAMS, teamLabel } from '@cxhub/shared'
import { useConfirmDialog } from './ConfirmDialog.jsx'
import { deleteSpamRulePrompt, toggleSpamRulePrompt } from './confirmPrompts.js'

function toApi(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    isActive: form.isActive,
    channel: form.channel || null,
    client: form.client || null,
    company: form.company || null,
    fromPatterns: form.fromPatterns.length ? form.fromPatterns : null,
    toPattern: form.toPattern.trim() || null,
    subjectPatterns: form.subjectPatterns.length ? form.subjectPatterns : null,
    bodyPattern: form.bodyPattern.trim() || null,
    bodyPatternType: form.bodyPatternType || 'substring',
    intent: form.intent || null,
    category: form.category.trim() || null,
  }
}

function fromRule(rule) {
  return {
    name: rule.name ?? '',
    description: rule.description ?? '',
    isActive: rule.isActive ?? true,
    channel: rule.channel ?? '',
    client: rule.client ?? '',
    team: rule.team ?? 'customer_service',
    company: rule.company ?? '',
    fromPatterns: rule.fromPatterns ?? [],
    toPattern: rule.toPattern ?? '',
    subjectPatterns: rule.subjectPatterns ?? [],
    bodyPattern: rule.bodyPattern ?? '',
    bodyPatternType: rule.bodyPatternType ?? 'substring',
    intent: rule.intent ?? '',
    category: rule.category ?? '',
  }
}

// Conditions column: labeled lines (To/Body/Intent) plus, for fromPatterns
// and subjectPatterns, a wrapped chip list sorted alphabetically -- readable
// at a glance even for a rule with many values (e.g. the AM Website "Auto
// Reply" rule ported from Bright Pattern's IVR), instead of one long
// "or"-chained sentence. buildConditionSummary() does the data shaping;
// this just renders it. From is shown before Subject, matching checkSpam()'s
// own evaluation order.
function ConditionsCell({ rule }) {
  const { lines, fromPhrases, subjectPhrases } = buildConditionSummary(rule)
  if (!lines.length && !fromPhrases.length && !subjectPhrases.length) {
    return <Typography variant="caption" color="text.secondary">(no conditions — disabled)</Typography>
  }
  return (
    <Stack spacing={0.5}>
      {fromPhrases.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            From contains:
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            {fromPhrases.map((p) => (
              <Chip key={p} size="small" variant="outlined" label={p} />
            ))}
          </Stack>
        </Box>
      )}
      {lines.map((l) => (
        <Typography key={l.label} variant="caption" sx={{ fontFamily: 'monospace' }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{l.label}:</Box> {l.text}
        </Typography>
      ))}
      {subjectPhrases.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            Subject contains:
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            {subjectPhrases.map((p) => (
              <Chip key={p} size="small" variant="outlined" label={p} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  )
}

function DragHandleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
    </svg>
  )
}

// Wraps a table row as a dnd-kit sortable item. The drag listeners are
// attached ONLY to the handle cell, never the row itself, so the row's own
// Switch/Edit/Delete controls keep working normally — dragging never
// competes with clicking them. disabled (a search/channel/intent filter is
// active) shows a greyed, inert handle with an explanatory tooltip instead
// of silently no-opping.
function SortableRuleRow({ rule, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id, disabled })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 36, px: 0.5 }}>
        {disabled ? (
          <Tooltip title="Clear search/filters to reorder rules">
            <Box sx={{ color: 'text.disabled', display: 'flex', cursor: 'not-allowed' }}>
              <DragHandleIcon />
            </Box>
          </Tooltip>
        ) : (
          <Box
            {...attributes} {...listeners}
            sx={{ color: 'text.secondary', display: 'flex', cursor: 'grab', touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
          >
            <DragHandleIcon />
          </Box>
        )}
      </TableCell>
      {children}
    </TableRow>
  )
}

export default function SpamRulesView() {
  const [askConfirm, confirmDialog] = useConfirmDialog()
  const [rules, setRules] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [createdCount, setCreatedCount] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [filterChannel, setFilterChannel] = useState('')
  const [filterClient, setFilterClient] = useState('')
  const [filterTeam, setFilterTeam] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const [filterIntent, setFilterIntent] = useState('')
  const [filterCategory, setFilterCategory] = useState('')

  async function load() {
    try {
      const [list, accountList] = await Promise.all([adminSpam.list(), adminSpam.accounts()])
      setRules(list)
      setAccounts(accountList)
    } catch (e) {
      console.error('Failed to load spam rules', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  // Sets company+client+channel together from one registered account (e.g.
  // "1A eBay") -- a shortcut over picking all three dropdowns individually.
  // Clicking the currently-active account clears back to "All", same
  // toggle-off convention as the Company/Client/Channel filters' own "All
  // X" option.
  function selectAccount(account) {
    const isActive = filterCompany === account.company && filterClient === account.client && filterChannel === account.channel && filterTeam === (account.team ?? '')
    setFilterCompany(isActive ? '' : account.company)
    setFilterClient(isActive ? '' : account.client)
    setFilterChannel(isActive ? '' : account.channel)
    setFilterTeam(isActive ? '' : (account.team ?? ''))
  }

  function openNew() { setEditing(null); setDialogOpen(true) }
  function openEdit(rule) { setEditing(rule); setDialogOpen(true) }
  function closeDialog() { setDialogOpen(false); setEditing(null) }

  // saved is a single rule object for a plain single-value save (create OR
  // edit, whichever it was, when the fan-out combo count is exactly one) --
  // but an ARRAY when the fan-out produced more than one row. That can now
  // happen on an EDIT too (adding scope to a saved rule creates sibling
  // rows alongside the one being edited), not just on create, so this
  // branches on the actual shape returned rather than on wasEdit.
  function handleSaved(saved, wasEdit) {
    if (!Array.isArray(saved)) {
      setRules((prev) => prev.map((r) => r.id === saved.id ? saved : r))
    } else if (wasEdit) {
      const originalId = editing?.id
      const updatedOriginal = saved.find((r) => r.id === originalId)
      const newOnes = saved.filter((r) => r.id !== originalId)
      setRules((prev) => {
        const next = updatedOriginal ? prev.map((r) => r.id === originalId ? updatedOriginal : r) : prev
        return [...next, ...newOnes]
      })
      if (newOnes.length) setCreatedCount(newOnes.length)
    } else {
      setRules((prev) => [...prev, ...saved])
      if (saved.length > 1) setCreatedCount(saved.length)
    }
    closeDialog()
  }

  async function handleDelete(rule) {
    if (!(await askConfirm(deleteSpamRulePrompt(rule.name)))) return
    setDeleteBusy(true)
    try {
      await adminSpam.delete(rule.id)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (e) {
      console.error('Delete failed', e)
    } finally {
      setDeleteBusy(false)
    }
  }

  async function toggleActive(rule) {
    if (!(await askConfirm(toggleSpamRulePrompt(rule.name, rule.isActive)))) return
    try {
      const updated = await adminSpam.update(rule.id, { ...toApi(fromRule(rule)), isActive: !rule.isActive })
      setRules((prev) => prev.map((r) => r.id === updated.id ? updated : r))
    } catch (e) {
      console.error('Toggle failed', e)
    }
  }

  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // Reorders matching precedence, not just display — see admin-spam.js's
  // /rules/reorder route and spam.js's checkSpam() (first match wins).
  // Optimistic: updates local state immediately, reverts on failure.
  async function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = rules.findIndex((r) => r.id === active.id)
    const newIndex = rules.findIndex((r) => r.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const previous = rules
    const reordered = arrayMove(rules, oldIndex, newIndex)
    setRules(reordered)
    try {
      const updated = await adminSpam.reorder(reordered.map((r) => r.id))
      setRules(updated)
    } catch (e) {
      console.error('Reorder failed', e)
      setRules(previous)
    }
  }

  // No fixed enum for category (unlike Channel/Client/Company) -- offered as
  // suggestions/filter options from whatever's actually in use on the loaded
  // rules, so a newly-typed category shows up here with no code change.
  const categoryOptions = useMemo(
    () => [...new Set(rules.map((r) => r.category).filter(Boolean))].sort(),
    [rules]
  )

  const q = search.trim().toLowerCase()
  const filteredRules = rules.filter((r) => {
    if (q && !matchesSpamRuleSearch(r, search)) return false
    if (filterChannel && r.channel !== filterChannel) return false
    if (filterClient && r.client !== filterClient) return false
    if (filterTeam && (r.team ?? 'customer_service') !== filterTeam) return false
    if (filterCompany && r.company !== filterCompany) return false
    if (filterIntent && r.intent !== filterIntent) return false
    if (filterCategory && r.category !== filterCategory) return false
    return true
  })

  const hasFilters = q || filterChannel || filterClient || filterTeam || filterCompany || filterIntent || filterCategory

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      {confirmDialog}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Spam Rules</Typography>
          <Typography variant="body2" color="text.secondary">
            Conversations matching a rule are automatically closed with intent "Spam".
            All conditions on a rule must match; leave a field blank to skip it.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openNew}>+ New Rule</Button>
      </Stack>

      {/* Account shortcuts -- one chip per registered account (company x
          client, e.g. "1A eBay"), read live from companies.js's registry
          (adminSpam.accounts()) rather than hardcoded, so a newly-registered
          account (e.g. AM_WEBSITE) appears here automatically with no code
          change. Each chip just sets the three dropdown filters below
          together; it's a convenience over the same filtering, not a
          separate mechanism. */}
      {accounts.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
          {accounts.map((a) => {
            const isActive = filterCompany === a.company && filterClient === a.client && filterChannel === a.channel && filterTeam === (a.team ?? '')
            return (
              <Chip
                key={a.account}
                label={a.team === 'support' ? `${a.company} ${teamLabel(a.team)}` : `${a.company} ${a.client}`}
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                color={isActive ? 'primary' : 'default'}
                onClick={() => selectAccount(a)}
              />
            )
          })}
        </Stack>
      )}

      {/* Search + filter bar */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search by ID, name, subject, or body…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Channel</InputLabel>
          <Select value={filterChannel} label="Channel" onChange={(e) => setFilterChannel(e.target.value)}>
            <MenuItem value="">All channels</MenuItem>
            {KNOWN_CHANNELS.map((c) => <MenuItem key={c} value={c}>{CHANNEL_LABELS[c] ?? c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Client</InputLabel>
          <Select value={filterClient} label="Client" onChange={(e) => setFilterClient(e.target.value)}>
            <MenuItem value="">All clients</MenuItem>
            {KNOWN_CLIENTS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Team</InputLabel>
          <Select value={filterTeam} label="Team" onChange={(e) => setFilterTeam(e.target.value)} SelectDisplayProps={{ 'data-testid': 'spam-filter-team' }}>
            <MenuItem value="">All teams</MenuItem>
            {TEAMS.map((t) => <MenuItem key={t} value={t}>{teamLabel(t)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Company</InputLabel>
          <Select value={filterCompany} label="Company" onChange={(e) => setFilterCompany(e.target.value)}>
            <MenuItem value="">All companies</MenuItem>
            {KNOWN_COMPANIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Intent</InputLabel>
          <Select value={filterIntent} label="Intent" onChange={(e) => setFilterIntent(e.target.value)}>
            <MenuItem value="">All intents</MenuItem>
            {intentOptionsFor([filterIntent, ...rules.map((r) => r.intent)]).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} label="Category" onChange={(e) => setFilterCategory(e.target.value)}>
            <MenuItem value="">All categories</MenuItem>
            {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        {hasFilters && (
          <Button size="small" variant="text" onClick={() => { setSearch(''); setFilterChannel(''); setFilterClient(''); setFilterTeam(''); setFilterCompany(''); setFilterIntent(''); setFilterCategory('') }}>
            Clear
          </Button>
        )}
        {hasFilters && (
          <Typography variant="caption" color="text.secondary">
            {filteredRules.length} of {rules.length} rule{rules.length !== 1 ? 's' : ''} — clear filters to drag-reorder
          </Typography>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : rules.length === 0 ? (
        <Typography color="text.secondary" sx={{ pt: 2 }}>
          No spam rules yet. Add one to start filtering unwanted messages.
        </Typography>
      ) : filteredRules.length === 0 ? (
        <Typography color="text.secondary" sx={{ pt: 2 }}>
          No rules match the current filters.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 32, px: 0.5 }} />
                    <TableCell sx={{ width: 72 }}>Active</TableCell>
                    <TableCell sx={{ width: 88 }}>ID</TableCell>
                    <TableCell sx={{ width: 76 }}>Company</TableCell>
                    <TableCell sx={{ width: 76 }}>Channel</TableCell>
                    <TableCell sx={{ width: 76 }}>Client</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 110 }}>Category</TableCell>
                    <TableCell>Conditions</TableCell>
                    <TableCell sx={{ width: 96 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRules.map((rule) => (
                    <SortableRuleRow key={rule.id} rule={rule} disabled={hasFilters}>
                      <TableCell>
                        <Tooltip title={rule.isActive ? 'Click to disable' : 'Click to enable'}>
                          <Switch
                            checked={rule.isActive}
                            onChange={() => toggleActive(rule)}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {rule.shortId ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={rule.company} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={CHANNEL_LABELS[rule.channel] ?? rule.channel} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Chip size="small" label={rule.client} variant="outlined" />
                          {/* Team (2026-10-07): shown only when not the default. */}
                          {rule.team === 'support' && <Chip size="small" label={teamLabel(rule.team)} variant="outlined" data-testid="spam-rule-team-chip" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{rule.name}</Typography>
                        {rule.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {rule.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rule.category && <Chip size="small" label={rule.category} variant="outlined" />}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        <ConditionsCell rule={rule} />
                        {rule.intent && (
                          <Box mt={0.5}>
                            <Chip size="small" label="Checked after classification" variant="outlined" color="warning" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(rule)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(rule)} disabled={deleteBusy}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </SortableRuleRow>
                  ))}
                </TableBody>
              </Table>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      <SpamRuleDialog
        open={dialogOpen}
        rule={editing}
        onClose={closeDialog}
        onSaved={handleSaved}
        categoryOptions={categoryOptions}
      />

      <Snackbar
        open={createdCount != null}
        autoHideDuration={4000}
        onClose={() => setCreatedCount(null)}
        message={createdCount != null ? `${createdCount} rules created` : ''}
      />
    </Box>
  )
}
